import { cn } from "@newxsystems/shared";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { TextEffect } from "../../../../text-effects/TextEffect.client";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import { HeroProbe } from "../HeroProbe/HeroProbe.client";
import { HeroSurface } from "../HeroSurface/HeroSurface.client";
import type { IHeroBackgroundImageProps } from "./HeroBackgroundImage.props";

/** 4 columns on phones, 8 on tablets, the full 12 on desktop. */
const GRID_COLUMNS = Array.from({ length: 12 }, (_, index) => ({
  key: index,
  visibility: index >= 8 ? "hidden lg:block" : index >= 4 ? "hidden md:block" : "block",
}));

export const HeroBackgroundImage = ({
  alignment,
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  image,
}: IHeroBackgroundImageProps) => {
  const centered = alignment === "center";
  return (
    <div
      className="relative isolate flex min-h-[34rem] flex-col overflow-hidden bg-background md:min-h-[42rem] lg:min-h-[47rem]"
      data-hero-root=""
    >
      <div className="absolute inset-0" data-hero-media="">
        <DaylightMedia
          className="h-full w-full rounded-none"
          framed={false}
          imageClassName="object-cover opacity-72 saturate-[0.62]"
          media={image.image}
          effect={image.effect}
          priority
          sizes="100vw"
        />
      </div>
      <HeroSurface />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-t from-void via-void/62 to-void/10"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-r from-void/85 via-void/15 to-transparent"
      />

      <div
        aria-hidden="true"
        className="daylight-container signal-grid-fade absolute inset-x-0 top-0 h-2/3"
      >
        <div className="grid h-full grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          {GRID_COLUMNS.map((column) => (
            <span
              className={cn("h-full border-r border-foreground/10", column.visibility)}
              key={column.key}
            />
          ))}
        </div>
      </div>

      <HeroProbe />

      <div className="daylight-container relative z-10 flex w-full flex-1 flex-col justify-end gap-9 pt-32 pb-7 md:gap-12 md:pb-10">
        <div
          className={cn(
            "daylight-hero-lines flex max-w-[62rem] flex-col gap-5 md:gap-6",
            centered && "mx-auto items-center text-center",
          )}
        >
          {eyebrow ? <p className="signal-label text-signal">{eyebrow}</p> : null}
          {heading ? (
            <h1 className="max-w-[15ch] break-words font-daylight-display text-[clamp(2.75rem,7.4vw,5.5rem)] font-semibold leading-[0.94] tracking-[-0.04em] text-balance text-foreground">
              <TextEffect effect={headingEffect} text={heading} />
            </h1>
          ) : null}
          {description ? (
            <DaylightRichText className="max-w-[54ch] text-ash" content={description} />
          ) : null}
          {buttons?.length ? (
            <div className={cn("flex flex-wrap gap-3 pt-2", centered && "justify-center")}>
              {buttons.map((button, index) => (
                <DaylightAction action={button} key={`${button.href}-${index}`} />
              ))}
            </div>
          ) : null}
        </div>

        <div className="daylight-hero-cue flex items-center justify-between gap-6 border-t border-border pt-5">
          <span className="signal-label text-ash">Scroll to explore</span>
          <span aria-hidden="true" className="font-daylight-mono text-sm leading-none text-signal">
            ↓
          </span>
        </div>
      </div>
    </div>
  );
};

HeroBackgroundImage.displayName = "Content::HeroBackgroundImage";
